import { useEffect } from "react";
import { Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { getDollarValue } from "../../helpers/currency";
import { HubHookFormInput } from "../../components";
import { getCogs } from "../QuoteCalcTable/helper";
import useQuoteEntities from "../QuoteCalcTable/useQuoteEntities";

import { BasicCardStack, StyledItemRow } from "./styles";
import ItemFields from "./ItemFields";

const DEFAULT_COMMISSION_RATE = 5;

const toNumber = (v) => Number(v) || 0;

const costFields = [
  { id: "mold", label: "Mold Cost" },
  { id: "heatTreat", label: "Heat Treat" },
  { id: "machining", label: "Machining" },
  { id: "destructive", label: "Destructive Testing" },
  { id: "xray", label: "X-Ray" },
  { id: "certifications", label: "Material Certifications" },
  { id: "international", label: "International" },
  { id: "other", label: "Other" },
];

export const QuoteItemsTable = () => {
  const { watch, setValue, getValues } = useFormContext();
  const entities = useQuoteEntities();

  // COGS from QuoteCalcTable calculations (calc id 25)
  const cogs = getCogs(watch, entities);

  // Sum of manually-entered cost fields (excludes commission — it's derived)
  const otherCostsTotal = costFields.reduce(
    (sum, f) => sum + toNumber(watch(`cost.${f.id}`)),
    0,
  );

  // Commission rate with default fallback
  const commissionRate = watch("comissionRate");
  const effectiveRate =
    commissionRate == null || commissionRate === ""
      ? DEFAULT_COMMISSION_RATE
      : Number(commissionRate);

  // Base for commission = COGS + other costs (commission doesn't apply to itself)
  const commissionBase = cogs + otherCostsTotal;
  const commission = (effectiveRate / 100) * commissionBase;

  // Calculated total = COGS + other costs + commission
  const calculatedTotal = commissionBase + commission;

  // Seed commissionRate default on first render if unset
  useEffect(() => {
    if (commissionRate == null || commissionRate === "") {
      setValue("comissionRate", DEFAULT_COMMISSION_RATE);
    }
  }, []);

  // Keep cost.comission synced with the derived value
  useEffect(() => {
    setValue("cost.comission", Number(commission.toFixed(2)));
  }, [commission, setValue]);

  // Auto-fill the editable `total` field with the calculated value — but only
  // when it's empty. Once a value is saved or typed, we never clobber it.
  // To re-sync with the current calculation, clear the Total input and the
  // effect will populate it from the next calculatedTotal tick.
  useEffect(() => {
    const current = getValues("total");
    const isEmpty = current == null || current === "" || Number(current) === 0;
    if (isEmpty && calculatedTotal > 0) {
      setValue("total", Number(calculatedTotal.toFixed(2)));
    }
  }, [calculatedTotal, getValues, setValue]);

  return (
    <BasicCardStack sx={{ padding: 0, gap: 0 }} justifyContent="space-between">
      <Stack>
        <StyledItemRow header includePadding>
          <Typography variant="caption">Description</Typography>
          <Typography variant="caption">Value</Typography>
        </StyledItemRow>

        <ItemFields />

        <StyledItemRow sx={{ gridAutoRows: "25px", borderBottom: "0px" }}>
          {costFields?.map((cost) => (
            <>
              <Typography variant="caption" className="ItemCell-root">
                {cost?.label}
              </Typography>
              <HubHookFormInput
                name={`cost.${cost?.id}`}
                placeholder="0.00"
                shouldUnregister={false}
                disableHelperText
                isCurrency
              />
            </>
          ))}
          <Stack
            display="grid"
            gridTemplateColumns="2fr 50px 1fr 100px"
            gridColumn="span 2"
            alignItems="center"
            gap="15px"
          >
            <Typography variant="caption" className="ItemCell-root">
              Comission Rate
            </Typography>
            <HubHookFormInput
              name="comissionRate"
              placeholder="5"
              defaultValue={DEFAULT_COMMISSION_RATE}
              shouldUnregister={false}
              endAdornment="%"
              disableHelperText
              isDecimal
            />
            <Typography variant="caption" className="ItemCell-root">
              Comission
            </Typography>
            <HubHookFormInput
              name="cost.comission"
              placeholder="0.00"
              shouldUnregister={false}
              disableHelperText
              isCurrency
            />
          </Stack>
          <Typography variant="caption" className="ItemCell-root">
            Calculated Total
          </Typography>
          <Typography variant="caption" className="ItemCell-root">
            {getDollarValue(calculatedTotal)}
          </Typography>
          <Typography variant="boldCaption" className="ItemCell-root">
            Total
          </Typography>
          <HubHookFormInput
            name="total"
            placeholder="0.00"
            shouldUnregister={false}
            disableHelperText
            isCurrency
          />
        </StyledItemRow>
      </Stack>
    </BasicCardStack>
  );
};

export default QuoteItemsTable;
